import {
  RESTRIKTION_CLASSES,
  RESTRIKTION_LABEL,
  formatDate,
  notisDateLabel,
  notisEffectiveDatum,
  notisEffectiveInFuture,
  type RestriktionTyp,
} from "@/lib/status";
import type { NoticeEvent } from "@/lib/api";
import { ExternalLink, CalendarDays, MapPin, Bell, RefreshCw, Ban } from "lucide-react";
import { Link } from "@tanstack/react-router";

type Props = {
  events: NoticeEvent[];
  onSelectKommun?: (kommunkod: string) => void;
};

const NOTIS_LABEL: Record<NoticeEvent["notis_typ"], string> = {
  introduktion: "Restriktion publicerad",
  uppdatering: "Uppdatering",
  upphavning: "Restriktion upphävd",
};

function getNotisLabel(
  notis_typ: NoticeEvent["notis_typ"],
  restriktion_typ: string | null | undefined,
  future: boolean
): string {
  if (notis_typ === "introduktion" && restriktion_typ === "forbud")
    return future ? "Bevattningsförbud införs" : "Bevattningsförbud införd";
  if (notis_typ === "introduktion" && restriktion_typ === "rekommendation")
    return future ? "Rekommendation utfärdas" : "Rekommendation utfärdad";
  if (notis_typ === "upphavning")
    return future ? "Restriktion upphör" : "Restriktion upphävd";
  return NOTIS_LABEL[notis_typ];
}

const NOTIS_ICON: Record<NoticeEvent["notis_typ"], typeof Bell> = {
  introduktion: Bell,
  uppdatering: RefreshCw,
  upphavning: Ban,
};

function EventCard({
  event,
  onSelectKommun,
}: {
  event: NoticeEvent;
  onSelectKommun?: (kommunkod: string) => void;
}) {
  const typ = event.restriktion_typ as RestriktionTyp | null;
  const isUpphavning = event.notis_typ === "upphavning";
  const cls =
    isUpphavning
      ? { bg: "bg-[color:var(--status-none-soft)]", text: "text-[color:var(--status-none)]", border: "border-[color:var(--status-none)]/30", dot: "bg-[color:var(--status-none)]" }
      : typ
        ? RESTRIKTION_CLASSES[typ]
        : RESTRIKTION_CLASSES.rekommendation;
  const Icon = NOTIS_ICON[event.notis_typ];

  const kommunerText = event.kommuner.map((k) => k.namn).join(", ");

  // Always show the effective date (Börjar gälla/Gäller sedan or Upphör/Upphörde)
  const future = notisEffectiveInFuture(event.notis_typ, event.datum_fran, event.datum_till);
  const effectiveDate = notisEffectiveDatum(event.notis_typ, event.datum_fran, event.datum_till);
  const dateLabel = notisDateLabel(event.notis_typ, event.datum_fran, event.datum_till);

  return (
    <div className={`rounded-xl border ${cls.border} ${cls.bg} p-4`}>
      <div className="flex items-start gap-3">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cls.dot} bg-opacity-20`}
        >
          <Icon className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-semibold ${cls.text}`}>
              {getNotisLabel(event.notis_typ, event.restriktion_typ, future)}
            </span>
            {event.restriktion_typ && event.notis_typ !== "introduktion" && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-sm text-muted-foreground">
                  {RESTRIKTION_LABEL[event.restriktion_typ as RestriktionTyp]}
                </span>
              </>
            )}
          </div>

          {event.kommuner.length > 0 && (
            <div className="mt-1 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Link
                to="/kommun/$kommunkod"
                params={{ kommunkod: event.kommuner[0].kommunkod }}
                onClick={() => onSelectKommun?.(event.kommuner[0].kommunkod)}
                className="text-sm font-medium text-foreground hover:underline text-left truncate"
              >
                {kommunerText}
              </Link>
            </div>
          )}

          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            <span>
              {dateLabel}: {effectiveDate ? formatDate(effectiveDate) : "Omgående"}
            </span>
          </div>

          {event.omraden_text && (
            <p className="mt-2 text-xs text-foreground/80 line-clamp-2">
              {event.omraden_text}
            </p>
          )}

          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {event.datum_publicerad ? `Publicerad: ${formatDate(event.datum_publicerad)}` : ""}
            </span>
            {event.url && (
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Källa <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function EventsFeed({ events, onSelectKommun }: Props) {
  if (events.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
        Inga händelser att visa.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          onSelectKommun={onSelectKommun}
        />
      ))}
    </div>
  );
}
